/**
 * Pipeline logic: deals, contacts, companies and activities.
 *
 * `crm` throughout is `{ companies, contacts, deals, acts }` — the same shape
 * the storage layer loads and saves.
 */
import { parseDate, daysAgo, daysTo, startOfDay } from '../lib/dates.js';
import { cycleMonths } from './pricing.js';

/*
 * ---------------------------------------------------------------- indexes
 *
 * Every lookup below used to walk its whole list.
 *
 * That is fine for one call and ruinous for the way these pages actually
 * ask: the Deals board asks which client each of 161 deals became, and it
 * asks several times over - for the tiles, for the split into tabs, for the
 * value totals, for the alert sweep. Against a thousand clients that was
 * 405ms of string comparison per render, and a render happens on every
 * keystroke in a filter box. Off an index it is 15ms.
 *
 * Keyed on the array itself in a WeakMap, so nothing has to be invalidated:
 * the store replaces these arrays when the data changes, which drops the
 * old index with them, and every call site stays as it was.
 */
const INDEXES = new WeakMap();

/*
 * `tag` is not decoration; it is what stops two indexes over one list from
 * being the same index.
 *
 * The cache was keyed on the array alone, so a second caller passing `rows`
 * with a different builder did not get a second index — it silently got the
 * first one, and then reached for a method that shape does not have. That
 * shipped: the pipeline crashed with "ix.get is not a function" the moment a
 * second builder went near the client list.
 *
 * The builder function itself cannot be the key, because most of them are
 * written inline and are a fresh identity on every call, which would mean
 * never hitting the cache at all. A name is the one thing that is stable and
 * that a reader can see is unique.
 */
function indexed(list, tag, build) {
  if (!list) return build([]);
  let byTag = INDEXES.get(list);
  if (!byTag) { byTag = new Map(); INDEXES.set(list, byTag); }
  if (!byTag.has(tag)) byTag.set(tag, build(list));
  return byTag.get(tag);
}

const byIdOf = list => indexed(list, 'byId', l => new Map(l.map(x => [x.id, x])));

export const companyOf = (crm, id) => (id ? byIdOf(crm.companies).get(id) : null) || null;
export const contactOf = (crm, id) => (id ? byIdOf(crm.contacts).get(id) : null) || null;
export const dealOf = (crm, id) => (id ? byIdOf(crm.deals).get(id) : null) || null;
export const companyName = (crm, id) => (companyOf(crm, id) || {}).name || '';

/** Activities on a deal, newest first. */
/**
 * The last thing anybody wrote about a deal, and who wrote it.
 *
 * A row on Deals is a name, an amount and a date — none of which says why it
 * is there. The note somebody left when they moved it off the board is the
 * answer, and it was written into the trail where the Deals page never
 * looked. Returns `{ text, who, at }` or null.
 *
 * The newest note with something in it, not simply the newest activity: half
 * the trail is subjects with an empty body ("Call", "Moved to Demo") and
 * showing one of those under the name says less than showing nothing.
 */
export const lastNote = (crm, dealId) => {
  /* Forwards, not backwards: dealActivities returns newest first, so walking
     from the end found the OLDEST note with a body — a deal with three notes
     showed the first one anybody ever wrote. */
  const acts = dealActivities(crm, dealId);
  for (let i = 0; i < acts.length; i += 1) {
    const a = acts[i];
    const text = String(a.notes || '').trim();
    if (text) return { text, who: a.who || '', at: a.due || '', subject: a.subject || '' };
  }
  return null;
};

export const dealActivities = (crm, dealId) => {
  /* Grouped once for the whole list rather than filtered per deal: the
     stall check asks this of every open row on the board. */
  const by = indexed(crm.acts, 'actsByDeal', acts => {
    const m = new Map();
    acts.forEach((a, i) => {
      if (!a.dealId) return;
      /* `i` is the order they are held in, which is the order they were
         written: the store appends, and the server hands them back in the
         order it stored them. It is the only thing that separates two on
         the same day — see the sort. */
      const got = m.get(a.dealId);
      if (got) got.push([a, i]); else m.set(a.dealId, [[a, i]]);
    });
    /*
     * Newest first — and a day is not fine-grained enough to say which of
     * two is newest.
     *
     * An activity carries a date, not a time, so everything logged today
     * ties. A plain date sort is stable, so the tie went to whichever was
     * written first, and the row's "Last activity" kept showing the note
     * before the call somebody had just made — it only looked right after
     * a reload, when the server happened to hand them back the other way
     * round. Position breaks the tie, latest written wins.
     */
    m.forEach((list, k) => m.set(k, list
      .sort((x, y) => (parseDate(y[0].due) || 0) - (parseDate(x[0].due) || 0) || y[1] - x[1])
      .map(([a]) => a)));
    return m;
  });
  return by.get(dealId) || [];
};

/**
 * When was this deal last actually touched?
 *
 * Completed, and written by a person. The second half is the point: the app
 * writes a row every time a card moves, a deal is won or an owner is set, and
 * every one of those used to count as having spoken to the customer. Dragging
 * a card across the board cleared its own Stalled flag.
 *
 * Read by the quiet-for column, the stall check, Waiting longest, Untouched
 * and the bell — so this one line decides all five.
 */
export function lastTouch(crm, dealId) {
  const done = dealActivities(crm, dealId).filter(x => x.done && x.by !== 'system');
  return done.length ? parseDate(done[0].due) : null;
}

/*
 * One list, two pages, and a single moment that moves a row from one to the
 * other.
 *
 * The pipeline is the visitor tracker's demo requests and nothing else. Each
 * is worked up the stages; reaching **Won** converts it - `kind` flips to
 * 'deal' - and at that moment it leaves the pipeline and appears on the Deals
 * page. The Deals page is therefore what has been won, plus anything typed in
 * by hand: a deal somebody adds themselves is a deal by definition, since
 * nobody types in a prospect the tracker already holds.
 *
 * Registrations are on neither. Somebody who signed up is a client, with a
 * plan and a billing state, and the Clients module is where they belong.
 */
/** Typed onto the pipeline by hand, as opposed to a deal typed in on the
    Deals page: the source says so, and it is the one value the sync never
    writes. */
export const MANUAL_SOURCE = 'Manual';

/** Put on the pipeline by a person rather than a sync. The Manual source
    marks the ones typed in on the board, and still marks them once they are
    won and have become deals. But any row with no origin that is sitting on
    the board as a lead is a hand-made lead too, whatever its source says:
    one whose source had been changed from Manual used to show on the board
    while dropping out of every count about the board — the sidebar said 131
    and the page said 132. A deal typed straight in on the Deals page was
    never a lead and stays out. */
export const isManualLead = d => d.source === MANUAL_SOURCE || (!d.origin && !isDeal(d));

/** Brought in from a spreadsheet, as opposed to typed one at a time. Its own
    source, not Manual's, because the two answer different questions: "who put
    this here" and "where did the list come from". */
export const IMPORT_SOURCE = 'Imported';
export const isImportedLead = d => d.source === IMPORT_SOURCE;

/**
 * What kind of client this is to us, in one word — "TVC", say.
 *
 * Not the source. Source is where somebody came from and is the tracker's
 * to write; a segment is a label the business puts on a row, it arrives on
 * an imported list, and it travels with the row onto the Deals page and
 * onto the client record when the deal converts.
 *
 * Uppercased on the way in so "tvc", "TVC" and "Tvc" are one segment and
 * not three in the filter.
 */
export const segmentOf = d => String((d && d.segment) || '').trim().toUpperCase();

/** How the badge reads. The word on its own is a code; this is a sentence. */
export const segmentLabel = v => (v ? `${v} client` : '');

/** Every segment on the book, for a filter that only offers what exists. */
export function segmentsOf(crm, rows = []) {
  const seen = new Set();
  (crm.deals || []).forEach(d => { const v = segmentOf(d); if (v) seen.add(v); });
  (rows || []).forEach(r => { const v = segmentOf(r); if (v) seen.add(v); });
  return [...seen].sort();
}

export const isTracked = d => d.origin === 'vizadtraq';

/**
 * How a row got onto the book, in one word.
 *
 * Leads, deals and an uploaded list all live in one table — `deals` — told
 * apart by three columns that each answer a different question: `kind` says
 * which page it is on, `origin`/`source_system` says which system wrote it,
 * and `source` says where the person came from. Nothing said the plain thing
 * anybody actually asks of a row: did this come from the tracker, did
 * somebody type it, or did it come off a spreadsheet?
 *
 * Four answers, in the order they are common:
 *
 *   Tracker       the visitor tracker saw a form and wrote the row.
 *   Registration  the clients app; they signed up rather than enquired.
 *   Imported      a spreadsheet, through the pipeline's Import.
 *   Manual        somebody typed it in.
 *
 * The check is ordered because the marks overlap: an imported row has no
 * origin system *and* a source of its own, and only the source tells it from
 * one typed by hand.
 */
export const ORIGINS = ['Tracker', 'Registration', 'Contact', 'Imported', 'Manual'];

export const originOf = d => {
  if (d.origin === 'vizadtraq') return 'Tracker';
  if (d.origin === 'xyra') return 'Registration';
  /* Put on the board from a contact record — the Contacts page's own way on,
     and the one route that used to arrive looking exactly like something
     somebody had typed straight into the pipeline. */
  if (d.contact) return 'Contact';
  if (d.source === IMPORT_SOURCE) return 'Imported';
  return 'Manual';
};
export const isDeal = d => d.kind === 'deal';
export const dealsOf = crm => crm.deals.filter(isDeal);

/**
 * They told us not to come back.
 *
 * Read off a date rather than a stage, because a stage is a position on a
 * board a row is still on and the whole point of this one is that it has
 * left. Nothing counts it, nothing chases it, and it appears in exactly one
 * place: the tab that exists so the answer is written down somewhere rather
 * than lost when the row disappears.
 */
export const isDropped = d => !!(d && d.dropped);

/** Still being worked: not yet won, not lost, and not turned away. */
export const isWorking = d => d.stage !== 'Won' && d.stage !== 'Lost' && !isDropped(d);

/** The board's columns. Won is not one: a row that reaches Won has left for
    the Deals page, so a Won column could only ever be empty. */
export const pipelineStages = S => (S.stages || []).filter(s => s !== 'Won');

/* ---- The pipeline's display stage model (build brief §6) ----
 *
 * The brief shows five stages, each with sub-statuses, while the data still
 * holds the older stage names and keeps Won/Lost/dropped as facts of their own.
 * Rather than rewrite every place that reads stage==='Won' — and migrate the
 * data — this maps a record onto one of the five display stages for the
 * pipeline only. The underlying stage, and conversion, are untouched; this is a
 * reading of the data, never a write. The four forward stages are what the
 * Move-stage dialog can set; Closed is reached through winning, losing or
 * turning a lead away, each of which the app already has its own action for.
 */
/* The stages and their sub-statuses are configuration, not code: they live in
   settings (defaulted in constants/defaults.js) and every screen reads them from
   S.stages / S.subStatuses. These fallbacks are used only before settings have
   loaded, so nothing renders empty on the first paint. */
export const DISPLAY_STAGES = ['New', 'Demo', 'Proposal', 'Won', 'Lost'];
/** The stages the Move-stage dialog can set — the workspace's stages minus the
    two closers, which are reached through the win and turn-away actions. */
export const forwardStages = S => (S.stages || DISPLAY_STAGES).filter(s => s !== 'Won' && s !== 'Lost');
/** A stage's sub-statuses, from settings. */
export const subStatusesOf = (S, stage) => ((S.subStatuses || {})[stage] || []);

/* Which stage a record sits in. Won and Lost (and the turned-away rows, which
   are Lost) are read off the data; older stage names are folded into the current
   ones — Qualified into New, Negotiation into Proposal — so a row written before
   the merge still lands on a tile. The migration remaps the stored values too;
   this keeps the UI correct in the meantime and for any straggler. */
export function displayStage(d) {
  if (!d) return 'New';
  if (d.stage === 'Won') return 'Won';
  if (d.stage === 'Lost' || isDropped(d)) return 'Lost';
  return ({ New: 'New', Qualified: 'New', Demo: 'Demo', Proposal: 'Proposal', Negotiation: 'Proposal' })[d.stage] || d.stage || 'New';
}

/* The sub-status within that stage. Lost tells apart a lost deal, a lead nobody
   wanted, and one nobody could reach; the rest read the stored sub_status. New
   and Won carry none. */
export function subStatus(d) {
  const stage = displayStage(d);
  if (stage === 'Lost') {
    if (d.dropKind === 'quiet') return 'No response';
    if (isDropped(d)) return 'Not interested';
    return 'Lost';
  }
  if (stage === 'New' || stage === 'Won') return '';
  return d.subStatus || '';
}


const lc = v => String(v || '').trim().toLowerCase();

/**
 * Split an uploaded list into what belongs on the board and what does not.
 *
 * The rule is the one the sales team already works to: somebody who has
 * registered is a client, and a client is not a lead. Four hundred rows off a
 * spreadsheet will be half people we already sell to, and putting those on the
 * pipeline asks the team to chase their own customers — which is worse than
 * useless, because the row looks exactly like a real enquiry.
 *
 * Matched on the email address and nothing else. A company name is typed
 * differently every time it is typed ("Royal Shine Car Wash", "Royal Shine
 * Carwash LLC") and a phone number is written five ways; the address is the
 * one field that is the same string on both sides or is not the same person.
 *
 * A row with no address cannot be checked at all, so it is kept apart rather
 * than quietly assumed new — the caller decides, and the screen says how many.
 *
 * Nothing is written here. It returns five lists and the caller shows them
 * before anything is imported, because an import that silently dropped rows
 * is worse than no check: nobody would know what was thrown away.
 */
export function sortImport(list, clientEmails = [], boardEmails = []) {
  const known = new Set(clientEmails.map(lc).filter(Boolean));
  const already = new Set(boardEmails.map(lc).filter(Boolean));
  const seen = new Set();

  const out = { fresh: [], clients: [], onBoard: [], repeats: [], noEmail: [] };

  (list || []).forEach((r, i) => {
    const row = {
      line: i + 1,
      company: String(r.company || '').trim(),
      email: lc(r.email),
      phone: String(r.phone || '').trim(),
      /* Carried through the check, not just past it. This function rebuilds
         the row rather than passing the caller's through, so anything it
         does not name is dropped on the floor - which is how the segment
         read off the file reached the screen and then vanished before the
         import wrote it. */
      segment: String(r.segment || '').trim().toUpperCase()
    };
    /* Nothing in any of the three columns: a blank line in the sheet, not a
       row that failed a check. */
    if (!row.company && !row.email && !row.phone) return;

    if (!row.email) { out.noEmail.push(row); return; }
    if (known.has(row.email)) { out.clients.push(row); return; }
    if (already.has(row.email)) { out.onBoard.push(row); return; }
    /* The same address twice in one file. Common in exported lists, and the
       second copy is not a second lead. */
    if (seen.has(row.email)) { out.repeats.push(row); return; }
    seen.add(row.email);
    out.fresh.push(row);
  });

  return out;
}

/** What the board draws: not yet won, and not a registration - the tracker's
    demo requests, plus anything added to the pipeline by hand. */
export const onPipeline = d => !isDeal(d) && d.origin !== 'xyra';
/*
 * What the pipeline actually shows.
 *
 * `rows` is the client book, and leaving it out is not a smaller question —
 * it is a different one. Without it the sweep cannot run, so a registered
 * customer who also enquired is counted; with it they are not. The sidebar
 * badge read 403 against a board of 351 for exactly that reason: two places
 * answering "how many leads" with two rules.
 *
 * Optional because three callers genuinely do not have the book to hand and
 * are asking the looser question — what came in at all, including people who
 * have since become customers. Those pass nothing and get what they always
 * got; anything that has to agree with the board passes `rows`.
 */
export const boardRows = (crm, rows) => crm.deals
  .filter(onPipeline)
  .filter(d => !isDropped(d))
  .filter(d => !(rows && customerOfLead(rows, d)));

/** Everything that was ever on the board, wins included - what the funnel and
    the win rate are about. The board is the subset still being worked. */
export const pipelineRows = crm => crm.deals.filter(d => isTracked(d) || isManualLead(d));

export const isOpen = d => onPipeline(d) && isWorking(d);

/** A deal can override its stage's default probability. */
export const probOf = (S, d) =>
  (d.prob != null && d.prob !== '') ? +d.prob : ((S.probs || {})[d.stage] || 0);

export const weighted = (S, d) => (+d.value || 0) * probOf(S, d) / 100;

export const openDeals = crm => crm.deals.filter(isOpen);

/**
 * The top of the funnel: what the tracker brought in and what became of it.
 *
 * The conversion rate counts only what has been decided. A demo request still
 * being worked is not a failure, only unfinished, so it belongs to neither
 * half — otherwise every request that arrived this morning would drag the rate
 * down on the day it landed.
 */
export function leadStats(S, crm, rows) {
  const history = pipelineRows(crm);
  const won = history.filter(d => d.stage === 'Won');
  const lost = history.filter(d => d.stage === 'Lost');
  const decided = won.length + lost.length;

  return {
    total: history.length,
    tracked: history.length,
    /* The same list the board draws, so the sidebar badge and the card
       cannot disagree. See boardRows. */
    open: boardRows(crm, rows).filter(isWorking).length,
    won: won.length,
    lost: lost.length,
    /* Converted and won are the same event: reaching Won is what makes it a
       deal. Kept as its own figure because the two can drift - a row can be
       marked Won while the API call that flips `kind` is still in flight. */
    converted: history.filter(isDeal).length,
    rate: decided ? Math.round(won.length / decided * 100) : null,
    byStage: (S.stages || []).map(stage => ({
      stage,
      deals: history.filter(d => d.stage === stage)
    }))
  };
}

/**
 * The client record for a won deal, if the conversion happened. Takes a deal
 * or a bare deal id.
 *
 * There are two halves to the link. `deal.clientId` is a local column, so it
 * survives a connected clients app taking over the client read path; the
 * client-side `dealId` is all offline mode records. Prefer the durable half.
 */
/*
 * The client list, indexed three ways — and there is only ever one of these.
 *
 * `indexed` caches on the array alone, so two callers passing the same list
 * with different builders get whichever shape was built first. A second
 * builder over `rows` therefore does not produce a second index; it silently
 * returns the first one, and the caller reaches for a method that is not on
 * it. That shipped, and the pipeline crashed with "ix.get is not a function".
 *
 * So the builder lives here, once, with a name. Anything that needs to look a
 * client up calls this rather than writing its own.
 */
const clientIndex = rows => indexed(rows, 'clients', list => {
  const byId = new Map();
  const byDeal = new Map();
  const byEmail = new Map();
  list.forEach(r => {
    byId.set(r.id, r);
    if (r.dealId) byDeal.set(r.dealId, r);
    const e = String(r.email || '').trim().toLowerCase();
    /*
     * Every account under the address, not the first one seen.
     *
     * Two do share one here — Algedra and Capital Associated are both
     * shameer@algedra.ae, both on AED 950 — and taking the first meant
     * whichever the clients app happened to return first, which is not a
     * rule, it is luck. The tie is broken by name in clientOfDeal.
     */
    if (e) {
      const had = byEmail.get(e);
      if (had) had.push(r); else byEmail.set(e, [r]);
    }
  });
  return { byId, byDeal, byEmail };
});

export const clientOfDeal = (rows, deal) => {
  const d = typeof deal === 'string' ? { id: deal } : (deal || {});
  if (!d.id && !d.clientId && !d.lead_email && !d.leadEmail) return undefined;

  const ix = clientIndex(rows);

  const linked = (d.clientId && ix.byId.get(d.clientId)) || ix.byDeal.get(d.id);
  if (linked) return linked;

  /*
   * Third way: the address.
   *
   * Neither link is ever made when the person signs up themselves - they go
   * to the site, pay, and appear in the client book with nothing pointing
   * back at the deal they came from. The board then said "0 moved to
   * clients, 44 still to move" about forty-four people who had all moved,
   * which is a figure that teaches everyone to ignore the figure.
   *
   * An address is the same person by any reasonable reading, so it counts,
   * and it is what the sales board already uses to keep a customer off the
   * lead list. See trackerSync.js.
   */
  const email = String(d.lead_email || d.leadEmail || '').trim().toLowerCase();
  if (!email) return undefined;

  const sharing = ix.byEmail.get(email);
  if (!sharing) return undefined;
  if (sharing.length === 1) return sharing[0];

  /*
   * More than one account under that address, so the address alone
   * cannot say which. The deal's own name can: it is either the
   * customer's trading name or the company it was filed under, and one
   * of the accounts will be called that.
   *
   * Nothing matching means nothing is returned. A deal credited to the
   * wrong one of two identical-looking accounts is worse than one
   * credited to neither, because only the second is visibly missing.
   */
  const key = v => String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

  /* All under one name is one company entered more than once — three of
     XYRA Technologies, two of Transtech — and any of them will do. Twelve
     of the thirty-four shared addresses here are that. */
  if (new Set(sharing.map(r => key(r.name))).size === 1) return sharing[0];

  const want = [key(d.title), key(d.company)].filter(Boolean);
  return sharing.find(r => want.includes(key(r.name)))
    || sharing.find(r => want.some(w => w && key(r.name).includes(w)))
    || undefined;
};

/**
 * Is this person on the books at all?
 *
 * A different question from clientOfDeal, and the difference matters.
 * clientOfDeal asks *which account this deal became*, so it refuses to answer
 * when an address carries several: crediting a deal to the wrong one of three
 * is worse than crediting it to none, because only the second is visibly
 * missing.
 *
 * The board asks something weaker and more useful — are we about to sell to
 * our own customer. Any account under the address settles that. Three
 * accounts share Ceo@pmrpetrol.com here, under three different trading names;
 * clientOfDeal could not pick one, so a registered customer stayed on the
 * pipeline looking exactly like a fresh enquiry.
 *
 * The account this very row created does not count. Pushing a converted deal
 * back onto the board is a deliberate act, and the client record it made on
 * the way out would otherwise hide it the moment it arrived.
 */
export const customerOfLead = (rows, deal) => {
  const exact = clientOfDeal(rows, deal);
  if (exact) return String(exact.dealId || '') === String(deal.id) ? undefined : exact;

  const email = String((deal && (deal.lead_email || deal.leadEmail)) || '').trim().toLowerCase();
  if (!email) return undefined;

  const sharing = clientIndex(rows).byEmail.get(email) || [];
  /* Any of them, except one this row made itself. */
  return sharing.find(r => String(r.dealId || '') !== String(deal.id));
};

/**
 * Has this deal been turned into a client? A deal carrying a clientId counts
 * as converted even when that account is not in the loaded list — archived,
 * or on a page the ledger has not fetched.
 */
export const isConverted = (rows, deal) =>
  !!(deal && deal.clientId) || !!clientOfDeal(rows, deal);

/**
 * What a deal is worth, and whether that is known at all.
 *
 * Money belongs to the clients app, which is the billing system: what
 * somebody pays is true there and nowhere else. Copying it onto the deal
 * would go stale the day they upgrade, so a deal that became a client reads
 * it live, the same way the row reads their name.
 *
 * Zero and unknown are different answers and used to look identical. A Free
 * plan really is worth nothing, and saying AED 0 about it is a fact; a
 * Starter client whose amount nobody has filled in is worth something we
 * cannot state, and saying AED 0 about them is a lie that tots up. Fifteen
 * of one and nineteen of the other read as "AED 0" on one tile, which is
 * how the page came to claim the business had no value at all.
 */
export function dealValue(rows, deal) {
  const client = clientOfDeal(rows, deal);

  /* Not a client yet, so the only figure is whatever somebody forecast. A
     forecast carries no billing cycle, so it is read as a monthly figure. */
  if (!client) {
    const typed = +deal.value || 0;
    return {
      amount: typed, monthly: typed, cycle: '', paid: false,
      known: typed > 0, free: false, from: 'forecast'
    };
  }

  const amount = Number(client.amount || 0);
  const paid = /^yes$/i.test(String(client.pay_status || ''));

  /* Free unless they have actually paid. One account sits on the FREE plan
     and has paid 49 — the plan name on its own would have thrown that away. */
  if (/^free$/i.test(String(client.plan || '')) && !(paid && amount > 0)) {
    return { amount: 0, monthly: 0, cycle: '', paid: false, known: true, free: true, from: 'plan' };
  }

  /* The amount on the record is what one invoice is, not what a month is:
     an annual 950 and a monthly 49 are each stored as their own number.
     Adding those gave a year plus two months printed as one figure, so
     totals are built from the monthly rate instead — the same division the
     Clients module has always used for MRR. */
  const months = cycleMonths(client.cycle);
  return {
    amount,
    monthly: months ? amount / months : amount,
    cycle: String(client.cycle || ''),
    paid,
    known: amount > 0,
    free: false,
    from: 'client'
  };
}

/**
 * The same over a list: what can be added up, and what could not be.
 *
 * `monthly` is the figure worth showing, and `annual` is it times twelve.
 * There is deliberately no sum of the stated amounts here: that number mixes
 * years with months, and the moment it exists somebody prints it.
 */
export function dealValueTotal(rows, list) {
  const out = { monthly: 0, annual: 0, known: 0, unknown: 0, free: 0, paid: 0, annualBilled: 0 };
  (list || []).forEach(d => {
    const v = dealValue(rows, d);
    if (v.free) { out.free += 1; return; }
    if (!v.known) { out.unknown += 1; return; }
    out.monthly += v.monthly;
    out.known += 1;
    if (v.paid) out.paid += 1;
    if (cycleMonths(v.cycle) > 1) out.annualBilled += 1;
  });
  out.annual = out.monthly * 12;
  return out;
}

/** A deal is stalled when nobody has logged contact inside the stall window. */
export function isStalled(S, crm, d) {
  if (!isOpen(d)) return false;

  /*
   * Nothing logged yet is not the same as gone quiet.
   *
   * A demo request that came in this morning has no completed activity
   * against it — nobody has had time — and `!t` alone called every one of
   * them stalled on the day it arrived, which put a red flag on the whole
   * board and taught people to ignore the flag. With nothing logged, the
   * clock runs from the day the row arrived instead.
   */
  const t = lastTouch(crm, d.id) || parseDate(d.created);
  return !t || daysAgo(t) > (S.stallDays || 7);
}

export function crmStats(S, crm, rows) {
  const open = openDeals(crm);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  /* The tracker's history, wins included - not every row in the table. A
     registration is a client, and counting one as lost business would make
     the win rate a figure about people nobody ever tried to sell to. */
  const board = pipelineRows(crm);
  const won = board.filter(d => d.stage === 'Won');
  const lost = board.filter(d => d.stage === 'Lost');
  const wonThisMonth = won.filter(d => {
    const c = parseDate(d.close);
    return c && c >= monthStart;
  });
  const closed = won.length + lost.length;

  return {
    open: open.length,
    pipeline: open.reduce((t, d) => t + (+d.value || 0), 0),
    forecast: open.reduce((t, d) => t + weighted(S, d), 0),
    wonMonth: wonThisMonth.reduce((t, d) => t + (+d.value || 0), 0),
    winRate: closed ? Math.round(won.length / closed * 100) : null,
    avg: won.length ? won.reduce((t, d) => t + (+d.value || 0), 0) / won.length : 0,
    leads: crm.contacts.filter(c => c.status !== 'Customer' && c.status !== 'Unqualified').length,
    acts: crm.acts.filter(a => !a.done).length,
    stalled: open.filter(d => isStalled(S, crm, d)).length,
    unconverted: board.filter(d => d.stage === 'Won' && !isConverted(rows, d)).length
  };
}

/** Everyone who can own something — configured owners plus anyone on a deal. */
/**
 * Who may be given a row.
 *
 * The Owners list in Settings, and nothing else. It used to fold in every
 * name already sitting on a deal so an existing owner could not be lost
 * from the dropdown — but the names that turned up that way were Super
 * Admin and Agalya, put there by the promote route rather than chosen by
 * anybody, and they read as members of a sales team they are not on.
 *
 * `current` keeps the safety where it matters: an editor passes the row's
 * own owner, so a name off the list is still offered on the row that has
 * it and saving cannot quietly wipe it. A filter passes nothing.
 */
export const salesTeam = (S, crm, current) =>
  [...new Set([...(S.owners || []), current].filter(Boolean))];

/*
 * Who runs the board, and who works a slice of it.
 *
 * Read off the role name rather than a permission, because handing a lead to
 * somebody is not a thing the API has a verb for — it is a value in a column
 * anybody who can edit a deal could already set. What it decides here is who
 * is *shown* the controls, and what a login is shown has to match what the
 * person expects of their own job title.
 *
 * Matched loosely on purpose. This workspace calls them Owner, Admin,
 * Manager, Agent and Viewer; the live one calls them Sales Manager and Sales
 * Executive, and a third will invent something else tomorrow — roles are a
 * list the workspace edits. Anything that reads like it runs a team runs the
 * team; everything else works what it was given.
 *
 * Nobody signed in (offline, or /auth/me has not landed) counts as running
 * it. The alternative is an empty board for a second on every load, which
 * looks like data loss.
 */
const RUNS_A_TEAM = /owner|admin|manager|head|director|lead|supervisor|super/i;
export const managesLeads = me => !me || RUNS_A_TEAM.test(me.role || '');

/**
 * The names a lead can be handed to.
 *
 * Accounts, not `S.owners`. That list is free text typed into settings and
 * has nothing to do with who can sign in, so a lead assigned off it went to
 * a name with no login behind it and was never seen again. Viewers are left
 * out — they cannot act on what they would be given — and the settings list
 * is the fallback for offline, where there are no accounts to read.
 */
/*
 * Sales, and nobody else.
 *
 * The workspace has thirty-one accounts across branding, SEO, product and
 * support, and every one of them was in this list — so handing over a lead
 * meant scrolling past two dozen people who will never open the pipeline, and
 * picking the wrong one silently parked the lead with somebody who does not
 * work it.
 *
 * Matched on the role's wording rather than a fixed list of titles, because
 * roles are a list the workspace edits: "Sales Manager", "Sales Executive",
 * "Senior Sales Exec" and whatever gets added next all say sales in them.
 *
 * Admins are deliberately not offered. They can assign, and they run the
 * board, but they do not work leads — and putting the nine of them in the
 * list is most of what made it unusable. Fourteen people work this pipeline;
 * those are the fourteen names.
 */
const WORKS_LEADS = /sales|business development|\bbdm?\b/i;

export const assignees = (people, S) => {
  const live = (people || [])
    .filter(p => p && p.name && p.active !== 0 && p.role !== 'Viewer')
    .filter(p => WORKS_LEADS.test(p.role || ''))
    .map(p => p.name);
  return live.length
    ? [...new Set(live)].sort((a, b) => a.localeCompare(b))
    : [...new Set(S && S.owners ? S.owners : [])];
};

/* --------------------------------------------------------------- filters */

const textMatch = (q, t) => !q || String(t).toLowerCase().includes(q.toLowerCase());

/* Settings, for the one filter that needs a threshold (stalled). The store
   sets this once per render; filterDeals keeps its signature. */
let S_FOR_TILES = {};
export const setTileSettings = S => { S_FOR_TILES = S || {}; };

/**
 * The shared half of every filter below: free text aside, the same four
 * questions are asked of a lead, a deal, a contact and an activity, and asking
 * them in one place is what stops one page honouring a control another page
 * quietly ignores.
 *
 * `from`/`to` measure when the row arrived, both ends inclusive. A row with no
 * date cannot be inside a window, so it drops out as soon as one end is set -
 * the same rule the client list uses, and for the same reason: "between these
 * dates" cannot include something that has no date.
 */
/**
 * One filter value, or several, against a row's one.
 *
 * The facet pills let more than one be picked — "Ads and Referral", "Demo
 * Request and Chatbot" — and several are stored as one comma-separated
 * string rather than an array. That is not tidiness: cF is shared by five
 * pages, half of which still set these from a single-value dropdown, and a
 * string keeps both kinds of control writing the same shape. A dropdown
 * simply never puts a comma in it.
 *
 * Nothing picked matches everything, which is what an empty filter means.
 */
export const picked = v => String(v || '').split(',').map(x => x.trim()).filter(Boolean);

const oneOf = (filter, value) => {
  const want = picked(filter);
  return !want.length || want.includes(value);
};

export function matchesCommon(cF, row, { owner, date }) {
  if (cF.owner && (owner || '') !== cF.owner) return false;
  if (!cF.from && !cF.to) return true;

  const on = parseDate(date);
  if (!on) return false;
  const day = startOfDay(on);
  if (cF.from && day < startOfDay(parseDate(cF.from))) return false;
  if (cF.to && day > startOfDay(parseDate(cF.to))) return false;
  return true;
}

/**
 * Deals, or leads — `kind` says which, and defaults to deals.
 *
 * The two live in one list because one becomes the other, so every caller has
 * to say which side of that line it is drawing. Leaving it to the caller to
 * filter afterwards is what let the deal board count leads.
 */
/**
 * What a stat tile narrows the list to, keyed by the tile. A tile is a count
 * with a list behind it; clicking one should show that list, so each answers
 * the same question its number did.
 */
export function matchesTile(S, crm, rows, d, tile) {
  if (!tile) return true;
  const week = () => {
    const at = parseDate(d.created);
    return !!at && (Date.now() - at.getTime()) < 7 * 86400000;
  };
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  /* The board's tiles count what is still being worked, so the lists they
     open leave the Lost column out - otherwise "Unowned 10" opened twelve. */
  switch (tile) {
    case 'week': return isWorking(d) && week();
    case 'unowned': return isWorking(d) && !d.owner;
    case 'stalled': return isStalled(S, crm, d);
    case 'month': { const c = parseDate(d.close); return !!c && c >= monthStart; }
    case 'linked': return !!clientOfDeal(rows, d);
    case 'unlinked': return !clientOfDeal(rows, d);
    default: return true;
  }
}

/**
 * Which list is being drawn, in one word.
 *
 * 'pipeline' and 'deal' are the two boards. Suffixing either with ':dropped'
 * asks for the ones somebody turned away instead — the same rows, the other
 * side of the line, and never both at once. Said in the kind rather than in
 * `cF` because cF is shared by five pages and a stray flag on it would empty
 * a board somewhere else.
 */
export function filterDeals(crm, cF, kind = 'deal', rows = []) {
  const [board, only] = String(kind).split(':');
  /* 'pipeline' is the board - tracked and not yet won; 'deal' is the Deals
     page - won, or typed in by hand. A row is on one or the other, never
     both, and a registration is on neither. */
  /* 'board' is the unified single-table view (build decision 2026-10): every row
     that lives in the deals table — open leads, won/lost deals and turned-away
     rows alike — except product registrations, which are clients, not leads.
     The pipeline uses it so New · Demo · Proposal · Won · Lost are all one board. */
  const want = board === 'pipeline' ? onPipeline
    : board === 'board' ? (d => d.origin !== 'xyra')
      : isDeal;
  const dropped = only === 'dropped';

  return crm.deals
    .filter(want)
    /*
     * Turned away, or not. One or the other, never mixed: a row somebody has
     * been told to stop chasing has no business in a count of open work, and
     * a board that quietly hid it with no way to look would lose the reason
     * along with the row.
     */
    /* The unified board shows turned-away rows too (under Lost); the two
       single-purpose boards keep them on their own side of the line. */
    .filter(d => board === 'board' ? true : isDropped(d) === dropped)
    /*
     * And nobody who already pays.
     *
     * A registration that also enquired is a customer, not a lead. Leaving
     * them on the board asks the team to sell to their own account — and the
     * team does, because the row looks exactly like the other fourteen
     * hundred. Matched on email through the same index the Deals page uses,
     * so the two screens can never disagree about who is a client.
     *
     * The account this very row created does not count. Somebody who pushes
     * a converted deal back onto the pipeline has said, deliberately, that
     * they want to work it again — and the client record it made on the way
     * out would otherwise hide it the moment it arrived, which reads as the
     * push having silently failed. What is being swept off is people who
     * registered on their own, not our own paperwork.
     *
     * The deal board is deliberately not swept: "moved to clients" is a tab
     * there, and it is the tab whose whole job is to list them.
     */
    .filter(d => !(board === 'pipeline' && !dropped && customerOfLead(rows, d)))
    .filter(d => {
      /* The linked contact counts as part of the row for search: a lead
         added from a contact carries the contact's name, and a search for
         that name must find it whatever the row's own title says. */
      const ct = d.contact ? contactOf(crm, d.contact) : null;
      const hay = `${d.title} ${companyName(crm, d.cid)} ${d.owner || ''} ${d.notes || ''} ${d.source || ''}`
        /* The tracker's own address, so searching for somebody by email finds
           them whether or not anybody has made them a contact yet. */
        + ` ${d.leadEmail || ''} ${d.leadType || ''}`
        + (ct ? ` ${ct.name || ''} ${ct.email || ''} ${ct.phone || ''}` : '');
      if (!textMatch(cF.q, hay)) return false;
      if (cF.stage && d.stage !== cF.stage) return false;
      /* 'manual' is the absence of a sync, not a value any row carries. */
      if (cF.origin && (cF.origin === 'manual' ? !!d.origin : d.origin !== cF.origin)) return false;
      if (!oneOf(cF.source, d.source)) return false;
      /* The label the row travels under. Compared uppercased, because the
         file it came off might have said "tvc". */
      if (cF.segment && segmentOf(d) !== String(cF.segment).toUpperCase()) return false;
      if (cF.plan && d.plan !== cF.plan) return false;
      /*
       * What the tracker saw, as filters.
       *
       * Type and state come off their own columns; country and campaign are
       * read through leadDetails so a row synced before those columns existed
       * still answers from its notes rather than dropping out of every
       * filtered view at once.
       */
      /*
       * Every form they filled in, not only the one the row is filed under.
       *
       * lead_type is a single column holding the strongest capture, because
       * that is what the row is labelled with and what an index can be built
       * on. Somebody who booked a demo AND used the chatbot is filed under
       * Demo Request - so asking for Chatbot hid them, although they had a
       * chatbot conversation sitting in the sheet. "Show me the chatbot
       * enquiries" has to mean all of them.
       *
       * Same for the state: a lead with two captures can be Submitted on one
       * and Partially Submitted on the other, and is honestly both.
       */
      if (cF.leadType || cF.leadState) {
        const i = d.intel;
        const types = (i && i.types && i.types.length) ? i.types : [d.leadType].filter(Boolean);
        const states = (i && i.captures && i.captures.length)
          ? [...new Set(i.captures.map(c => c.state))]
          : [d.leadState].filter(Boolean);
        /* Any of the picked ones, against any of this lead's captures. Two
           kinds chosen means "either", which is what a row of pills that can
           both be pressed looks like it should mean. */
        if (cF.leadType && !types.some(t => oneOf(cF.leadType, t))) return false;
        if (cF.leadState && !states.some(x => oneOf(cF.leadState, x))) return false;
      }
      if (cF.campaign && leadDetails(crm, d).campaign !== cF.campaign) return false;
      /*
       * Are they already a customer?
       *
       * The board carries registrations now, and a registration that went
       * through is somebody with an account: 263 of the 273 submitted ones
       * are in the clients book. They belong on the list - the team asked for
       * the whole list - but "who is left to sell to" is the question a
       * morning of calls starts with, and it needed a click.
       *
       * Matched on the address the tracker captured, the same way the Client
       * chip on the row is.
       */
      if (cF.customer) {
        const isClient = !!clientOfDeal(rows, d);
        if (cF.customer === "client" && !isClient) return false;
        if (cF.customer === "prospect" && isClient) return false;
      }
      if (!matchesTile(S_FOR_TILES, crm, rows, d, cF.tile)) return false;
      /* The date window asks when the enquiry came in, not when the sync
         wrote it down. See leadWhen(). */
      return matchesCommon(cF, d, { owner: d.owner, date: leadWhen(d) });
    })
    /*
     * Newest first, on both boards.
     *
     * It was worth descending, which on these rows is no order at all: a
     * demo request carries no value until somebody prices it, so every row
     * was zero and the list came out in whatever order the database
     * happened to answer in. A row added this minute landed somewhere in
     * the middle, and the one thing anybody wants to see after adding a row
     * is the row they added.
     *
     * Value breaks the tie, so a priced board still reads biggest-first
     * within a day.
     */
    /*
     * Newest first, by when they were last on the site — today at the top.
     *
     * This sorted on `created`, the row's own timestamp, which is the minute
     * the sync ran. A batch of four hundred rows all carry the same one, so
     * the board came out in whatever order MySQL felt like and the newest
     * enquiry was somewhere in the middle of it.
     *
     * Value still breaks the tie, so a priced board reads biggest-first
     * within a day.
     */
    .sort((a, b) => (parseDate(leadWhen(b)) || 0) - (parseDate(leadWhen(a)) || 0)
      || (+b.value || 0) - (+a.value || 0));
}

/** A contact still to be sold to: not yet a customer, not written off. */
export const isLeadContact = c => c.status !== 'Customer' && c.status !== 'Unqualified';

export function filterContacts(crm, cF) {
  return crm.contacts.filter(c => {
    if (!textMatch(cF.q, `${c.name} ${c.email || ''} ${c.phone || ''} ${companyName(crm, c.cid)}`)) return false;
    if (cF.status && c.status !== cF.status) return false;
    if (cF.source && c.source !== cF.source) return false;
    /* The tiles: a count with a list behind it. */
    if (cF.tile === 'leads' && !isLeadContact(c)) return false;
    if (cF.tile === 'customers' && c.status !== 'Customer') return false;
    if (cF.tile === 'unassigned' && c.owner) return false;
    return matchesCommon(cF, c, { owner: c.owner, date: c.created });
  });
}

export function filterCompanies(crm, cF) {
  return crm.companies.filter(c => {
    if (!textMatch(cF.q, `${c.name} ${c.city || ''} ${c.industry || ''}`)) return false;
    if (cF.industry && c.industry !== cF.industry) return false;
    return matchesCommon(cF, c, { owner: c.owner, date: c.created });
  });
}

export function filterActivities(crm, cF) {
  return crm.acts
    .filter(a => {
      if (!textMatch(cF.q, `${a.subject} ${a.who || ''} ${a.notes || ''}`)) return false;
      if (cF.status === 'Open' && a.done) return false;
      if (cF.status === 'Done' && !a.done) return false;
      /* An activity's date is the day it is due, not the day it was typed:
         "this week" on a task list means the work, not the paperwork. */
      return matchesCommon(cF, a, { owner: a.owner, date: a.due });
    })
    .sort((a, b) => (a.done - b.done) || ((parseDate(a.due) || 0) - (parseDate(b.due) || 0)));
}

/**
 * The journey, end to end, for the Sales overview.
 *
 * One shape rather than five loose counts, because every figure on that page
 * is a slice of the same story - a demo request arrives, somebody decides it
 * is worth selling to, the deal is won, the account appears in the clients
 * app - and figures computed apart drift apart.
 *
 * Nothing here is cached or seeded: it is recomputed from the live lists on
 * every render, so an empty workspace reads as empty rather than as a demo.
 */
export function salesJourney(S, crm, rows, { weeks = 12, trackedOnly = false } = {}) {
  /* The overview reads the tracker and nothing else; the board's own tiles
     count hand-added rows as well, since they are worked the same way. */
  const board = trackedOnly ? crm.deals.filter(isTracked) : pipelineRows(crm);
  const tracked = board;
  const won = board.filter(d => d.stage === 'Won');

  /* Weekly buckets, oldest first, each labelled by the Monday it starts. A
     week with nothing in it is still a week: dropping empty buckets would draw
     a line that skipped the quiet fortnight and lied about the slope. */
  const monday = d => {
    const x = startOfDay(d);
    x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
    return x;
  };
  const thisWeek = monday(new Date());
  const buckets = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const at = new Date(thisWeek);
    at.setDate(at.getDate() - i * 7);
    buckets.push({ at, key: at.getTime(), leads: 0, deals: 0 });
  }
  const byKey = new Map(buckets.map(b => [b.key, b]));

  const drop = (row, field) => {
    const at = parseDate(row.created);
    if (!at) return;
    const b = byKey.get(monday(at).getTime());
    if (b) b[field] += 1;
  };
  /*
   * Both series are dated by when the request arrived, not by when it closed.
   * A won row keeps its id and its arrival date - it was converted, not
   * re-created - so the second series is always a subset of the first and the
   * gap between the lines is that week's take-up. Dating the wins by their
   * close date instead would have drawn weeks with more wins than arrivals,
   * which reads as nonsense on its face.
   */
  board.forEach(d => drop(d, 'leads'));
  won.forEach(d => drop(d, 'deals'));

  /** Rank a dimension, biggest first, with anything blank folded into one. */
  const rank = (list, pick) => {
    const m = new Map();
    list.forEach(d => {
      const k = String(pick(d) || '').trim() || 'Direct';
      m.set(k, (m.get(k) || 0) + 1);
    });
    return [...m.entries()]
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  };

  return {
    /* The funnel proper. Each step is a subset of the one above it, which is
       what makes the step-to-step percentages mean anything. */
    funnel: [
      { label: 'On the pipeline', value: board.length, hint: 'demo requests tracked' },
      { label: 'Being worked', value: board.filter(d => d.stage !== 'New').length, hint: 'past the first stage' },
      { label: 'Won', value: won.length, hint: 'converted to a deal' },
      { label: 'On the ledger', value: won.filter(d => isConverted(rows, d)).length, hint: 'client record created' }
    ],
    weekly: buckets,
    bySource: rank(tracked, d => d.source),
    byStage: (S.stages || []).map(stage => ({
      label: stage,
      value: board.filter(d => d.stage === stage).length
    })),
    byOwner: rank(board.filter(isWorking), d => d.owner || 'Unassigned')
  };
}

/* ------------------------------------------------------------ lead facts */

/**
 * The ten kinds of form the visitor tracker can tell apart, in its own order
 * of priority — a form that mentions a demo and has a password box is a Demo
 * Request, not a Registration.
 *
 * The board only ever carries the sales-intent ones; the sync decides which
 * (VIZADTRAQ_LEAD_TYPES). The full list is here because the filter offers
 * whatever the rows actually carry, and because a row synced under an older
 * setting must still have a label when the setting changes underneath it.
 */
export const LEAD_TYPES = [
  'Demo Request', 'Enquiry/Quote', 'Contact', 'Registration', 'Tawk',
  'Chatbot', 'WhatsApp', 'Login', 'Newsletter', 'Unclassified Form'
];

/**
 * Did they finish the form?
 *
 * 'submitted' means the tracker watched it go through. 'partially_submitted'
 * means it did not — which covers somebody who typed two characters and left
 * *and* somebody whose thank-you page the tracker's detectors do not
 * recognise, and there is no way to tell those apart from here. A WhatsApp
 * click is always partial: nobody can see the other end of the conversation.
 *
 * So the board shows both and says which is which, rather than throwing away
 * the second group to make the first look tidy.
 */
export const LEAD_STATES = [
  ['submitted', 'Submitted'],
  ['partially_submitted', 'Partially Submitted']
];

export const leadStateLabel = s =>
  (LEAD_STATES.find(([v]) => v === s) || [])[1] || '';

/**
 * When this lead actually happened, as against when we found out.
 *
 * `created` is the row's own timestamp, which for anything the tracker
 * brought across is the moment the sync ran. Four hundred and ninety rows
 * landed inside the same two minutes, so ordering a board by it puts them in
 * no order at all and a "last 7 days" window matches every one of them or
 * none — both of which read as the filter being broken.
 *
 * The tracker knows the real answer twice over: when they first enquired, and
 * when they were last on the site. Last wins. Somebody who first came in July
 * and was back on the pricing page this morning is today's lead, and the board
 * is a list of who to call next.
 *
 * Rows added by hand, and rows synced before the intel column existed, have
 * only `created` — which for those is the truth, because somebody typed them
 * in that day.
 */
export function leadWhen(d) {
  const i = d.intel;
  return (i && (i.lastSeen || i.firstSeen)) || d.created || '';
}

/** How long since they were last on the site, in whole days. Null if unknown. */
export function leadQuiet(d) {
  const when = parseDate(leadWhen(d));
  return when ? daysAgo(when) : null;
}


/**
 * What the tracker actually told us about an enquiry, as fields.
 *
 * Three sources, strongest first.
 *
 *   1. The contact and company records the sync writes. A person's own record
 *      wins over anything the tracker guessed, always.
 *   2. `d.intel` — the tracker's whole answer, stored as JSON on the row:
 *      source and the five UTM tags, landing page and referrer, country,
 *      region and city, device, browser and OS, their Google profile, how many
 *      visits they made, and every form field they typed.
 *   3. The notes paragraph, for the rows written before (2) existed:
 *
 *        Demo Request requested 2026-09-09. Contact: Name · email · phone
 *        Wants Books · Campaign: x · Dubai · United Arab Emirates
 *        Landed on https://…
 *
 * The paragraph is still written, and still parsed, because a board full of
 * rows synced last month should not go blank the day the column arrives. It
 * only ever held four of these facts; everything past `campaign` comes from
 * the intel or comes back empty.
 */
/*
 * One answer per row, kept until the row changes.
 *
 * leadDetails splits the notes, runs three regexes over them and walks the
 * stored intel. Cheap once — 4ms over the whole board — and it was running
 * three or four times for the same row in a single render: once per tile,
 * again in the filter, again in the row itself.
 *
 * Keyed on the deal object. Deals are replaced rather than edited, so a
 * changed row misses the cache by itself; a WeakMap means a row that leaves
 * the board takes its entry with it. `crm` is checked too, because the
 * company and contact it reads through come from there.
 */
const LEAD = new WeakMap();

export function leadDetails(crm, d) {
  if (!d || typeof d !== 'object') return rawLeadDetails(crm, d);
  const hit = LEAD.get(d);
  if (hit && hit.crm === crm) return hit.v;
  const v = rawLeadDetails(crm, d);
  LEAD.set(d, { crm, v });
  return v;
}

function rawLeadDetails(crm, d) {
  const contact = d.contact ? contactOf(crm, d.contact) : null;
  const company = d.cid ? companyOf(crm, d.cid) : null;
  const notes = String(d.notes || '');
  let lines = notes.split('\n');

  /*
   * An earlier edit form pushed a "Contact: …" line above the tracker's
   * paragraph on every save, which moved the date, the campaign and the
   * landing page off the lines they are read from. Rows it touched are read
   * past that line, so their details come back without rewriting anything;
   * the contact in it is the newer one, so it wins.
   */
  let edited = '';
  if (/^Contact:/.test((lines[0] || '').trim()) && /^Demo requested \S+\./.test((lines[1] || '').trim())) {
    edited = lines[0].trim();
    lines = lines.slice(1);
  }
  const [line1 = '', line2 = '', line3 = ''] = lines;

  /* Written by an older sync as "Demo requested …", by the current one as
     "<Type> requested …". One expression reads both. */
  const REQUESTED = /^(.*?) requested (\S+)\./;

  const cut = (line, drop) => line.split(' · ').map(x => x.trim()).filter(x => x && !drop.test(x));
  const who = cut(edited || line1.replace(REQUESTED, '').trim(), /^Contact:/);
  const ctx = cut(line2, /^(Wants|Campaign:)/);

  const intel = d.intel || null;

  const email = (contact && contact.email) || (intel && intel.email)
    || who.find(x => x.includes('@')) || '';
  const phone = (contact && contact.phone) || (intel && intel.phone)
    || who.find(x => /^\+?[\d\s()-]{6,}$/.test(x)) || '';
  const requested = (intel && intel.firstSeen)
    || (line1.match(REQUESTED) || [])[2] || d.created || '';
  const campaign = (intel && intel.utm && intel.utm.campaign)
    || (line2.match(/Campaign: ([^·\n]+)/) || [])[1]?.trim() || '';
  const landing = (intel && intel.landing) || (line3.match(/^Landed on (\S+)/) || [])[1] || '';
  /* The company record wins: somebody may have corrected the city by hand,
     and the tracker's is only ever as good as the IP lookup behind it. */
  const city = (company && company.city) || (intel && intel.city) || ctx[0] || '';
  const country = (intel && intel.country) || (ctx.length > 1 ? ctx[ctx.length - 1] : '');

  /*
   * Every product asked for, not the shorthand.
   *
   * The sync writes `plan` as "Books +5" - the column is forty characters
   * and six product names do not fit - so the row can say how many but
   * never which. The full list is in this line of the notes, which is where
   * it has to be read back from: "+5" is a number standing in for five
   * things somebody wanted to know the names of.
   */
  const f = (intel && intel.fields) || {};
  const wants = f['products[]'] || f.products || f.plan
    || (line2.match(/Wants ([^·\n]+)/) || [])[1] || '';
  const productList = String(wants)
    .split(',').map(s => s.trim()).filter(Boolean)
    .map(s => s.replace(/^xyra[-_ ]?/i, '').replace(/[-_]/g, ' ').trim())
    .map(s => s.charAt(0).toUpperCase() + s.slice(1));

  const utm = (intel && intel.utm) || {};

  return {
    /* The row this came from. The page-journey window needs it: a list
       response carries the intel without its events, so the window reads
       that one deal in full when somebody opens it. */
    dealId: d.id,
    email, phone, requested, campaign, landing, city, country,
    /*
     * The path only: "/pricing" says more at a glance than the full URL.
     *
     * And without the query, which is not a detail. A real landing page off
     * an ad arrives as "/books/?utm_source=google&utm_medium=pmax&gad_source=
     * 2&gad_campaignid=24254652930&gclid=CjwKCAjwq8PVBhAKEiwA2i3SHW…" — two
     * hundred characters of tracking that would be printed on the card in
     * place of the two that say where they landed. The full URL is still what
     * the link goes to, and the tags are read off `utm` where they belong.
     */
    landingPath: landing
      ? landing.replace(/^https?:\/\/[^/]+/, '').split(/[?#]/)[0] || '/'
      : '',
    products: d.plan || '',
    productList,

    /*
     * The rest of what the Lead Intel API carries. Empty strings rather than
     * undefined throughout, so a card can print one without checking first,
     * and so a row synced before any of this existed renders identically to
     * one synced this morning — as blank, which is the truth about it.
     */
    type: d.leadType || (intel && intel.type) || '',
    state: d.leadState || (intel && intel.state) || '',
    stateLabel: leadStateLabel(d.leadState || (intel && intel.state)),
    /* Every kind of form this person has ever filled in, not just the one the
       row is filed under: somebody who booked a demo *and* opened WhatsApp is
       a different conversation from somebody who only did one. */
    types: (intel && intel.types) || [],
    /*
     * Every capture, with the state that went with it.
     *
     * types[] and states[] are two parallel lists that say nothing about
     * which went with which: somebody who booked a demo and abandoned a
     * chatbot has ["Demo Request","Chatbot"] and ["submitted","partially"],
     * and no way to tell from those two which of the pair they finished.
     * captures[] is the pairing, and it is what the row prints.
     */
    captures: (intel && intel.captures) || [],

    region: (intel && intel.region) || '',
    /*
     * "Muscat, Muscat, Oman".
     *
     * The tracker returns country, state and city, and for a city that is
     * also its own governorate the middle one repeats the last. Printed once,
     * in the order a person writes an address.
     */
    place: [...new Set([
      (company && company.city) || (intel && intel.city) || ctx[0] || '',
      (intel && intel.region) || '',
      (intel && intel.country) || (ctx.length > 1 ? ctx[ctx.length - 1] : '')
    ].filter(Boolean))].join(', '),
    referrer: (intel && intel.referrer) || '',
    device: (intel && intel.device) || '',
    browser: (intel && intel.browser) || '',
    os: (intel && intel.os) || '',
    /* The organisation behind their IP address. Named for what it is: the
       tracker calls it "company" and it is almost always the internet
       provider, which has put "Etisalat" on a card as an employer before. */
    network: (intel && intel.network) || '',
    /* The domain the tracker resolved for that organisation. Unlike the name
       it is occasionally the real employer rather than the provider - but it
       is still an IP lookup, so it is shown and never trusted. */
    networkDomain: (intel && intel.networkDomain) || '',
    /* The tracker's guess at their LinkedIn, made by Gemini from the address.
       Usually empty, occasionally wrong, and it was being stored and never
       shown - which is the worst of the three. */
    linkedin: (intel && intel.linkedin) || '',
    google: (intel && intel.google) || null,
    visits: (intel && intel.visits) || 0,
    lastSeen: (intel && intel.lastSeen) || '',
    /* The visit that brought them in: how long they stayed and how many pages
       they read. Two facts the tracker has always measured and the board has
       never shown, and the difference between a lead worth ringing and a
       bounce. */
    seconds: (intel && intel.seconds) || 0,
    pages: (intel && intel.pages) || 0,
    exit: (intel && intel.exit) || '',
    /* The ad they clicked, when they came through one. */
    ad: (intel && intel.ad) || null,
    /* Which social platform sent them. The tracker's "handle" is the campaign
       name on a paid click rather than an account, so it is a label. */
    social: (intel && intel.social) || [],
    adClicks: (intel && intel.adClicks) || [],
    sessions: (intel && intel.sessions) || [],
    sources: (intel && intel.sources) || (d.source ? [d.source] : []),
    utm: {
      source: utm.source || '', medium: utm.medium || '', campaign: utm.campaign || '',
      term: utm.term || '', content: utm.content || ''
    },
    /* Everything they typed that is not already a field of its own, so the
       detail sheet can list the message, the team size, the promo code. */
    fields: f
  };
}

/* ---------------------------------------------------------------- alerts */

/**
 * Pipeline-side alerts, merged with the client-side ones by the alert engine.
 * `kind` tells the bell which editor to open when the row is clicked.
 */
export function crmAlerts(S, crm, rows, currency = 'AED') {
  const out = [];
  const fmtMoney = v => `${currency} ${Math.round(v || 0).toLocaleString('en-US')}`;

  openDeals(crm).forEach(d => {
    const touch = lastTouch(crm, d.id);
    const close = parseDate(d.close);
    const value = +d.value || 0;

    if (!d.owner) {
      out.push({ id: `du:${d.id}`, sev: 3, tag: 'deal-unowned', kind: 'deal', cid: d.id,
        client: d.title, txt: `Deal has no owner · ${fmtMoney(value)}`, value: 0 });
    }
    if (close && daysTo(close) < 0) {
      out.push({ id: `dc:${d.id}`, sev: 3, tag: 'close-passed', kind: 'deal', cid: d.id,
        client: d.title, txt: `Close date passed ${Math.abs(daysTo(close))}d ago · ${fmtMoney(value)}`, value: 0 });
    } else if (!touch || daysAgo(touch) > (S.stallDays || 7)) {
      out.push({ id: `ds:${d.id}`, sev: 2, tag: 'deal-stalled', kind: 'deal', cid: d.id,
        client: d.title, txt: `No contact for ${touch ? `${daysAgo(touch)}d` : 'ever'} · ${fmtMoney(value)}`, value: 0 });
    } else if (!d.next) {
      out.push({ id: `dn:${d.id}`, sev: 1, tag: 'deal-nonext', kind: 'deal', cid: d.id,
        client: d.title, txt: 'No next step set', value: 0 });
    }
  });

  /* Deals only, here and above: `openDeals` already excludes leads, and a
     lead cannot reach 'Won' - its ladder ends at Qualified - but saying so is
     cheaper than relying on a stage list staying the way it is today. */
  dealsOf(crm)
    .filter(d => d.stage === 'Won' && !isConverted(rows, d))
    .forEach(d => out.push({
      id: `dv:${d.id}`, sev: 2, tag: 'deal-unconverted', kind: 'deal', cid: d.id,
      client: d.title, txt: 'Won but no client record yet', value: 0
    }));

  crm.acts.filter(a => !a.done).forEach(a => {
    const due = parseDate(a.due);
    if (!due) return;
    if (daysTo(due) < 0) {
      out.push({ id: `ao:${a.id}`, sev: 3, tag: 'act-overdue', kind: 'act', cid: a.id,
        client: a.subject, txt: `${a.type} overdue ${Math.abs(daysTo(due))}d${a.who ? ` · ${a.who}` : ''}`, value: 0 });
    } else if (daysTo(due) <= 2) {
      out.push({ id: `as:${a.id}`, sev: 2, tag: 'act-due', kind: 'act', cid: a.id,
        client: a.subject, txt: `${a.type} due in ${daysTo(due)}d`, value: 0 });
    }
  });

  return out;
}
